import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { styled, useStyletron } from 'baseui';
import { useAtomValue, useSetAtom } from 'jotai';
import { ACTION_BY, LABELLING_TYPE } from '../../../../common/constant/constant';
import MediaError from '../../../../common/media-error';
import MediaLoader from '../../../../common/media-loader';
import type { GetULabelConfigFunction } from '../../../../common/providers/feature-flag-provider';
import { mediaFileRefAtom } from '../../../labelling-v2/atoms/file-atoms';
import { EVENTS } from '../../../labelling-v2/atoms/label-store-dispatch/events';
import { isVideoLabelingM4EnabledAtom } from '../../../labelling-v2/atoms/m4-atoms';
import { multiVideoListAtom, selectedVideoIndexAtom } from '../../../labelling-v2/atoms/multi-video-atoms';
import { resolvedRelationshipsAtom } from '../../../labelling-v2/atoms/relationship-atoms';
import { useStageAtoms } from '../../../labelling-v2/components/stage-scope/hooks';
import { ANNOTATION_SHAPE_STYLING_TYPE, ANNOTATION_TYPE, BOUNDING_BOX_TOOLS, GRID_MODE, LAYER_ZINDEX, POLYGON_TOOLS, SEGMENTATION_TOOLS, TOOL } from '../../constants';
import { LINE_STYLE } from '../../constants/relationship-renderer';
import { ContentCanvasProvider } from '../../providers/content-canvas-provider';
import useShortcut from '../../providers/hooks/use-shortcut';
import useStage from '../../providers/hooks/use-stage';
import { CUSTOM_KEYBOARD_EVENTS, handleShortcutRegistrationError } from '../../providers/utils/shortcut';
import { EDITOR_MODE_TYPE } from '../../types';
import { buildEdgesFromRelationships } from '../../utils/relationship-renderer';
import Annotation from '../annotations/annotation';
import CanvasEditorWithBitmaps from '../annotations/canvas/canvas-editor-with-bitmaps';
import CanvasStage from '../annotations/canvas/canvas-stage';
import Cuboid from '../annotations/cuboid/cuboid';
import Ellipse from '../annotations/ellipse/ellipse';
import KeyPoint from '../annotations/key-point/key-point';
import MultiSelectRectangle from '../annotations/multi-select/multi-select-rectangle';
import Polygon from '../annotations/polygon/polygon';
import Polyline from '../annotations/polyline/polyline';
import BoundingBox from '../annotations/rectangle/bounding-box/bounding-box';
import Rectangle from '../annotations/rectangle/rectangle';
import SegmentationClick from '../annotations/segmentation-click/segmentation-click';
import { Skeleton } from '../annotations/skeleton/skeleton';
import { SkeletonWithTemplate } from '../annotations/skeleton/templates/skeleton-with-template';
import CrossHair from '../cross-hair/cross-hair';
import Grid from '../grid';
import Image from '../image/image';
import PDF from '../pdf/pdf';
import RelationshipRenderer from '../relationship-renderer/relationship-renderer';
import Snapping from '../snapping/snapping';
import { isLabelingMode } from '../toolbar/utils';
import { AnnotationVisibilityWrapper } from './annotation-visibility-wrapper';
import { VideoContainer } from './video-container';
const Layer = styled<'div', {
  $zIndex?: number | string;
  $isPreview?: boolean;
}>('div', ({
  $zIndex,
  $isPreview,
  $theme
}) => ({
  position: 'absolute',
  ...($isPreview ? {
    bottom: 0
  } : {
    top: 0
  }),
  left: 0,
  height: $isPreview ? '40%' : '100%',
  width: $isPreview ? '40%' : '100%',
  border: $isPreview ? `solid ${$theme.sizing.scale100} ${$theme.colors.borderOpaque}` : 'none',
  zIndex: $zIndex || 'auto'
}));
const EditorWrapper = styled('div', () => ({
  height: 'inherit',
  width: 'inherit'
}));
type PropsT = {
  readOnly: boolean;
  isVideoPlayer?: boolean;
  getULabelConfig?: GetULabelConfigFunction;
};
const Editor = ({
  readOnly,
  isVideoPlayer,
  getULabelConfig
}: PropsT) => {
  const [, theme] = useStyletron();
  const {
    setFileDimension,
    deleteAnnotation,
    deleteSelectedAnnotations,
    fitToScreen
  } = useStage();
  const {
    editableAnnotationAtom,
    selectedToolAtom,
    selectedAnnotationLabelAtom,
    selectedSubToolAtom,
    selectedAnnotationIdsAtom,
    multiSelectEnabledAtom,
    dispatchAtom,
    brushToolSettingsAtom,
    currentFrameDataAtom,
    editorModeAtom,
    gridModeAtom,
    snapEnabledAtom,
    fileTypeAtom,
    dataUrlAtom,
    isReviewFocusModeEnabledAtom,
    visibleRelationshipsAtom,
    relationshipEditorOpenAtom,
    visibilityModeAtom,
    focusedAnnotationIdAtom
  } = useStageAtoms();
  const dispatch = useSetAtom(dispatchAtom);
  const editableAnnotationFromAtom = useAtomValue(editableAnnotationAtom);
  const selectedTool = useAtomValue(selectedToolAtom);
  const annotationLabel = useAtomValue(selectedAnnotationLabelAtom);
  const selectedSubTool = useAtomValue(selectedSubToolAtom);
  const selectedAnnotationIds = useAtomValue(selectedAnnotationIdsAtom);
  const multiSelectEnabled = useAtomValue(multiSelectEnabledAtom);
  const selectedBrushTool = useAtomValue(brushToolSettingsAtom).selectedTool;
  const currentFrameData = useAtomValue(currentFrameDataAtom);
  const editorMode = useAtomValue(editorModeAtom);
  const gridMode = useAtomValue(gridModeAtom);
  const isSnapEnabled = useAtomValue(snapEnabledAtom);
  const fileType = useAtomValue(fileTypeAtom);
  const dataUrl = useAtomValue(dataUrlAtom);
  const isFocusModeEnabled = useAtomValue(isReviewFocusModeEnabledAtom);
  const visibleRelationships = useAtomValue(visibleRelationshipsAtom);
  const visibilityMode = useAtomValue(visibilityModeAtom);
  const focusedAnnotationId = useAtomValue(focusedAnnotationIdAtom);
  const isRelationshipEditorOpen = useAtomValue(relationshipEditorOpenAtom);
  const resolvedRelationships = useAtomValue(resolvedRelationshipsAtom);
  const [key, setKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  // eslint-disable-next-line react-hooks/purity
  const [imageKey, setImageKey] = useState(Date.now());
  const isGridEnabled = gridMode !== GRID_MODE.NONE && editorMode === EDITOR_MODE_TYPE.LABELING;
  const isPDFLabeling = fileType === LABELLING_TYPE.PDF;
  const isMultiSelectEnabled = multiSelectEnabled;
  const hasSelection = selectedAnnotationIds.length > 0;
  const isM4VisibilityActive = useAtomValue(isVideoLabelingM4EnabledAtom);
  const relatedAnnotationIds = useMemo(() => {
    if (!isM4VisibilityActive || visibilityMode?.type === 'default' || !visibilityMode) return new Set<string>();
    const related = new Set<string>();
    const relationships = [...visibleRelationships.map(([, relationship]) => relationship), ...(isRelationshipEditorOpen ? resolvedRelationships : [])];
    for (const relationship of relationships) {
      const sourceSelected = selectedAnnotationIds.includes(relationship.sourceAnnotationId);
      const targetSelected = selectedAnnotationIds.includes(relationship.targetAnnotationId);
      if (sourceSelected) related.add(relationship.targetAnnotationId);
      if (targetSelected) related.add(relationship.sourceAnnotationId);
    }
    return related;
  }, [isM4VisibilityActive, visibilityMode, visibleRelationships, isRelationshipEditorOpen, resolvedRelationships, selectedAnnotationIds]);
  const editableAnnotationId = editableAnnotationFromAtom;
  const {
    imageUrl,
    annotationStore,
    annotationMLStore,
    annotationConsensusStore
  } = currentFrameData ?? {
    imageUrl: '',
    annotationStore: {}
  };
  const edges = useMemo(() => {
    const savedEdges = buildEdgesFromRelationships(visibleRelationships, annotationStore, theme.colors.accent, LINE_STYLE.SOLID);
    if (!isRelationshipEditorOpen || resolvedRelationships.length === 0) return savedEdges;
    const draftEdges = buildEdgesFromRelationships(resolvedRelationships.map(r => [r.id, r]), annotationStore, theme.colors.accent, LINE_STYLE.DASHED);
    return [...savedEdges, ...draftEdges];
  }, [visibleRelationships, annotationStore, theme.colors.accent, isRelationshipEditorOpen, resolvedRelationships]);
  const isDrawing = !!annotationLabel && selectedTool !== TOOL.POINTER;
  const {
    registerHotKeys
  } = useShortcut();
  const onEscapePress = useCallback(event => {
    if (event.type === 'keydown') {
      setKey(key + 1);
    }
  }, [key]);
  const onDeletePress = useCallback(() => {
    if (readOnly) return;
    if (selectedAnnotationIds.length > 1) {
      deleteSelectedAnnotations();
    } else {
      selectedAnnotationIds.forEach(id => {
        const metaData = {
          actionDuration: '0',
          actionBy: ACTION_BY.KEYBOARD
        };
        deleteAnnotation(id, metaData);
      });
    }
  }, [deleteAnnotation, deleteSelectedAnnotations, selectedAnnotationIds, readOnly]);
  const mediaFileRef = useAtomValue(mediaFileRefAtom);
  const onAltPress = useCallback((event: KeyboardEvent) => {
    dispatch({
      type: EVENTS.TOGGLE_MULTI_SELECT,
      enabled: event.type === 'keydown'
    });
  }, [dispatch]);
  const onError = useCallback(() => {
    setLoading(false);
    setIsError(true);
    fitToScreen();
  }, [fitToScreen]);
  const selectedVideoIndex = useAtomValue(selectedVideoIndexAtom);
  const isMultiVideoIndex = selectedVideoIndex !== 0;
  const multiVideoList = useAtomValue(multiVideoListAtom);
  const isMultiVideo = multiVideoList.length > 1;
  const onLoad = useCallback((width: number, height: number) => {
    setLoading(false);
    setFileDimension(width, height);
  }, [setFileDimension]);
  const onVideoLoad = useCallback(() => {
    if (loading) {
      setLoading(false);
    }
    if (!isMultiVideo) return;
    setTimeout(() => {
      fitToScreen();
    }, 300);
  }, [loading, fitToScreen, isMultiVideo]);
  useEffect(() => {
    const {
      errors,
      removeHotKeys
    } = registerHotKeys([{
      keys: ['Backspace'],
      onAction: onDeletePress
    }, {
      keys: ['Escape'],
      onAction: onEscapePress
    }, {
      keys: ['Delete'],
      onAction: onDeletePress
    }, {
      keys: ['Alt'],
      onAction: onAltPress,
      type: CUSTOM_KEYBOARD_EVENTS.TOGGLE
    }]);
    errors.forEach(error => handleShortcutRegistrationError(error));
    return () => {
      removeHotKeys();
    };
  }, [onDeletePress, onEscapePress, registerHotKeys, onAltPress]);
  const getLabelingComponent = () => {
    if (isPDFLabeling) {
      return <ContentCanvasProvider>
          <PDF pdfUrl={dataUrl} key={imageKey} onLoad={onLoad} onError={onError} data-testid="pdf-boundingbox" />
        </ContentCanvasProvider>;
    }
    if (isVideoPlayer) {
      return <VideoContainer key={mediaFileRef} fitToScreen={fitToScreen} getULabelConfig={getULabelConfig} setFileDimension={setFileDimension} onVideoLoad={onVideoLoad} />;
    }
    return <Image imageUrl={imageUrl} key={imageKey} onLoad={onLoad} onError={onError} />;
  };
  return <EditorWrapper>
      {/* Handling of loading for video player is done internally inside Video
          component. Eventually this will be removed for all media types and moved to
          Overlay layer of stage to avoid scaling issues */}
      {loading && !isVideoPlayer && <MediaLoader />}
      {isError ? <MediaError onRetry={() => {
      setIsError(false);
      setLoading(true);
      setImageKey(Date.now());
    }} buttonText="Reload" /> : <Layer>{getLabelingComponent()}</Layer>}
      {isGridEnabled && <Layer data-testid="grid">
          <Grid color={gridMode === GRID_MODE.BLACK ? 'black' : 'gray'} />
        </Layer>}
      {!isError && !loading && !isMultiVideoIndex && <>
          <Layer $zIndex={selectedTool === TOOL.SEGMENTATION ? LAYER_ZINDEX : 'auto'}>
            <CanvasStage>
              <CanvasEditorWithBitmaps />
            </CanvasStage>
          </Layer>
          <Layer>
            <svg height="100%" width="100%" data-stage-svg="true">
              {isMultiSelectEnabled && selectedAnnotationIds.length > 1 && <MultiSelectRectangle />}
              {Object.keys(annotationStore).map(annotationIdx => {
            if (annotationIdx === editableAnnotationId) return null;
            return <AnnotationVisibilityWrapper key={`annotation-${annotationIdx}`} isSelected={selectedAnnotationIds.includes(annotationIdx)} isRelated={relatedAnnotationIds.has(annotationIdx)} isHovered={focusedAnnotationId === annotationIdx} isM4VisibilityActive={isM4VisibilityActive} hasSelection={hasSelection} visibilityMode={visibilityMode} annotation={annotationStore[annotationIdx]} isHidden={annotationStore[annotationIdx]?.hidden ?? false}>
                    <Annotation annotationIdx={annotationIdx}
              // @ts-expect-error todo(ts-migration) TS2322 Type 'StageAnnotation | undefined' is not assignable to type 'AnnotationT'.
              annotation={annotationStore[annotationIdx]} isFocusModeEnabled={isFocusModeEnabled} selectedAnnotationIds={selectedAnnotationIds || []} />
                  </AnnotationVisibilityWrapper>;
          })}
              {annotationMLStore && Object.keys(annotationMLStore).map(annotationIdx => {
            const annotation = {
              ...annotationMLStore[annotationIdx],
              annotationLabel: {
                // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
                ...annotationMLStore[annotationIdx].annotationLabel,
                // Add ML tag to show before label name
                // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
                title: `[ML] ${annotationMLStore[annotationIdx].annotationLabel.title}`
              }
            };
            return <AnnotationVisibilityWrapper key={`annotation-${annotationIdx}`} isSelected={selectedAnnotationIds.includes(annotationIdx)} isRelated={relatedAnnotationIds.has(annotationIdx)} isHovered={focusedAnnotationId === editableAnnotationId} isM4VisibilityActive={isM4VisibilityActive} hasSelection={hasSelection} visibilityMode={visibilityMode} annotation={annotationMLStore[annotationIdx]} isHidden={annotationMLStore[annotationIdx]?.hidden ?? false}>
                      <Annotation annotationIdx={annotationIdx}
              // @ts-expect-error todo(ts-migration) TS2322 Type '{ annotationLabel: { title: string; description?: string | undefined; style: AnnotationStyleT; questionnaire?: QuestionnaireT[] | undefined; max?: number | undefined; isRequired?: boolean | unde...
              annotation={annotation} disableChanges stylingType={ANNOTATION_SHAPE_STYLING_TYPE.DASHED_BORDER} isFocusModeEnabled={isFocusModeEnabled} selectedAnnotationIds={selectedAnnotationIds || []} />
                    </AnnotationVisibilityWrapper>;
          })}
              {annotationConsensusStore && Object.keys(annotationConsensusStore).map(annotationIdx => {
            const consensusAnnotation = annotationConsensusStore[annotationIdx];
            const annotator =
            // @ts-expect-error todo(ts-migration) TS18048 'consensusAnnotation' is possibly 'undefined'.
            consensusAnnotation.metadata?.consensusInfo?.annotator || 'Unknown';
            const annotation = {
              ...consensusAnnotation,
              annotationLabel: {
                // @ts-expect-error todo(ts-migration) TS18048 'consensusAnnotation' is possibly 'undefined'.
                ...consensusAnnotation.annotationLabel,
                // Add annotator tag to show before label name
                // @ts-expect-error todo(ts-migration) TS18048 'consensusAnnotation' is possibly 'undefined'.
                title: `[Annotator ${annotator}] ${consensusAnnotation.annotationLabel.title}`
              }
            };
            return <Annotation key={`consensus-annotation-${annotationIdx}`} annotationIdx={annotationIdx}
            // @ts-expect-error todo(ts-migration) TS2322 Type '{ annotationLabel: { title: string; description?: string | undefined; style: AnnotationStyleT; questionnaire?: QuestionnaireT[] | undefined; max?: number | undefined; isRequired?: boolean | unde...
            annotation={annotation} disableChanges stylingType={ANNOTATION_SHAPE_STYLING_TYPE.DASHED_BORDER} isFocusModeEnabled={isFocusModeEnabled} selectedAnnotationIds={selectedAnnotationIds || []} />;
          })}
              {/*
               Render the selected annotation at last so it appears at top.
               In some cases vertices are hidden behind newer drawn shapes
               zIndex doesn't work inside SVG.
               */}
              {typeof editableAnnotationId === 'string' && editableAnnotationId !== null && annotationStore[editableAnnotationId] && <AnnotationVisibilityWrapper key={`annotation-${editableAnnotationId}`} isSelected={selectedAnnotationIds.includes(editableAnnotationId)} isRelated={relatedAnnotationIds.has(editableAnnotationId)} isHovered={focusedAnnotationId === editableAnnotationId} isM4VisibilityActive={isM4VisibilityActive} hasSelection={hasSelection} visibilityMode={visibilityMode} annotation={annotationStore[editableAnnotationId]} isHidden={annotationStore[editableAnnotationId]?.hidden ?? false}>
                    <Annotation key={`selected-${editableAnnotationId}`} annotationIdx={editableAnnotationId} annotation={annotationStore[editableAnnotationId]} />
                  </AnnotationVisibilityWrapper>}
              {!isMultiSelectEnabled && selectedAnnotationIds.length > 1 && <MultiSelectRectangle />}
              {isDrawing && <g key={`drawing_${Object.keys(annotationStore).length + key}`} data-testid="drawing-annotation">
                  {(() => {
              switch (selectedTool) {
                case ANNOTATION_TYPE.RECTANGLE:
                  return selectedSubTool !== BOUNDING_BOX_TOOLS.YOLO_BOUNDING_BOX ? <Rectangle isDrawing /> : null;
                case ANNOTATION_TYPE.ELLIPSE:
                  return <Ellipse isDrawing />;
                case ANNOTATION_TYPE.KEY_POINT:
                  return <KeyPoint isDrawing />;
                case ANNOTATION_TYPE.POLYLINE:
                  return <Polyline isDrawing />;
                case ANNOTATION_TYPE.CUBOID:
                  return <Cuboid isDrawing />;
                case ANNOTATION_TYPE.SKELETON:
                  {
                    const skeletonTemplates = annotationLabel?.toolConfigs?.[TOOL.SKELETON]?.skeletonTemplates;
                    // currently we support only one skeleton template per label. will update if needed for multiple templates per label case as triggering UX will also change.
                    const firstTemplate = skeletonTemplates?.[0];
                    return firstTemplate ? <SkeletonWithTemplate isDrawing templateConfig={firstTemplate} /> : <Skeleton isDrawing />;
                  }
                case ANNOTATION_TYPE.POLYGON:
                  {
                    switch (selectedBrushTool) {
                      case POLYGON_TOOLS.SAM_CLICK:
                        return <SegmentationClick isPolygon />;
                      case POLYGON_TOOLS.SAM_BOUNDING_BOX:
                        return <BoundingBox isPolygon />;
                      default:
                        return <Polygon isDrawing />;
                    }
                  }
                case ANNOTATION_TYPE.SEGMENTATION:
                  {
                    switch (selectedBrushTool) {
                      case SEGMENTATION_TOOLS.SAM_BOUNDING_BOX:
                        return <BoundingBox />;
                      case SEGMENTATION_TOOLS.SAM_CLICK:
                        return <SegmentationClick />;
                    }
                  }
              }
            })()}
                </g>}
              <RelationshipRenderer edges={edges} />
            </svg>
          </Layer>
        </>}

      {isDrawing && isLabelingMode(editorMode) && !isMultiVideoIndex && <Layer data-testid="crosshair" style={{
      pointerEvents: 'none'
    }}>
          <CrossHair />
        </Layer>}
      {isSnapEnabled && isLabelingMode(editorMode) && !isMultiVideoIndex && <Layer data-testid="snapping" style={{
      pointerEvents: 'none'
    }}>
          <Snapping />
        </Layer>}
    </EditorWrapper>;
};
export default Editor;